import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import CookieConsent from "./CookieConsent";
import ContactModal from "./ContactModal";

export default function Layout({ children }: { children: React.ReactNode }) {
    const [isContactOpen, setIsContactOpen] = useState(false);

    // Global Event Listener for Contact Modal + Universal Link Interceptor
    useEffect(() => {
        const handleOpen = () => setIsContactOpen(true);

        // Intercept clicks on any element with href="#contact" or "contact:open"
        const handleGlobalClick = (e: MouseEvent) => {
            const target = (e.target as HTMLElement).closest('a, button');
            if (target) {
                const href = target.getAttribute('href') || target.getAttribute('data-href');
                if (href === '#contact' || href === 'contact:open') {
                    e.preventDefault();
                    setIsContactOpen(true);
                }
            }
        };

        window.addEventListener('open-contact', handleOpen);
        window.addEventListener('click', handleGlobalClick, { capture: true }); // Capture phase to preempt router

        return () => {
            window.removeEventListener('open-contact', handleOpen);
            window.removeEventListener('click', handleGlobalClick, { capture: true });
        };
    }, []);

    // Analytics Tracker
    useEffect(() => {
        const trackVisit = async () => {
            // Check session to avoid spamming the log on every refresh
            if (sessionStorage.getItem('session_logged')) return;

            try {
                const api = process.env.API_URL || 'http://localhost:3001';
                await fetch(`${api}/api/analytics`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        page: window.location.pathname,
                        screenSize: `${window.innerWidth}x${window.innerHeight}`
                    })
                });
                sessionStorage.setItem('session_logged', 'true');
            } catch (e) {
                console.error("Tracking error", e);
            }
        };

        trackVisit();
    }, []);

    return (
        <div className="min-h-screen relative overflow-hidden selection:bg-[var(--accent)] selection:text-white">
            <ContactModal
                isOpen={isContactOpen}
                onClose={() => setIsContactOpen(false)}
            />
            <CookieConsent />

            {/* GLOBAL CRYSTAL NOISE OVERLAY */}
            <div className="fixed inset-0 z-[9999999] bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.035] pointer-events-none" />

            {/* CONTINUOUS ANIMATED BACKGROUND BLOB (Framer Motion Only) */}
            <motion.div
                className="fixed top-[-10%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-[var(--accent)] pointer-events-none opacity-[0.07] blur-[120px]"
                animate={{
                    x: [0, 100, 0],
                    y: [0, 50, 0],
                    scale: [1, 1.2, 1]
                }}
                transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "linear"
                }}
            />
            <motion.div
                className="fixed bottom-[-10%] right-[-10%] w-[40vw] h-[40vw] rounded-full bg-[var(--accent)] pointer-events-none opacity-[0.05] blur-[100px]"
                animate={{
                    x: [0, -70, 0],
                    y: [0, -50, 0],
                    scale: [1, 1.1, 1]
                }}
                transition={{
                    duration: 25,
                    repeat: Infinity,
                    ease: "linear",
                    delay: 2
                }}
            />

            <motion.main
                className="relative z-10 w-full min-h-screen"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
            >
                {children}
            </motion.main>
        </div>
    );
}
